import { Grid, Icon, Link, Option, Heading, Text, useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import clsx from 'clsx'
import React, { FunctionComponent, ReactNode, useEffect, useRef } from 'react'
import { ModuleContainer, ShippingAddressItem, ShippingOptionError } from '../../component'
import { editShippingAddressRoute, Route, useNavigateToShippingAddress } from '../../config/router'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { AddressModeType, ShippingAddressStatus, ShippingOptionErrorType, TerminalErrorCode } from '../../model'
import { TrackingEvent } from '../../model/monitoring'
import {
  clearShippingOptionError,
  setShippingOptionError,
  useAppDispatch,
  useAppRouter,
  useCheckoutState,
  useIsDisabledUpdatingCheckout,
  useIsPickup,
  useShippingAddress,
  useShippingOption,
  useIsShippingAddressValid,
  useIsDeferredShipping,
  updateCheckout,
  CheckoutAppDispatch,
  useInitialData,
} from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { isUndefinedOrEmpty } from '@afterpay/utils/string'

import styles from './style.module.css'
import { unwrapResult } from '@reduxjs/toolkit'
import { isValidAddress } from '../../utils/profile-validation'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { Trans, useTranslation } from '../../utils/convergence'
import { TooltipMessage } from './TooltipMessage'
import { Money } from '@afterpay/types'

// Unlikely that we'd need to use this. Used for TS purposes
const defaultMoney: Money = { amount: '0', currency: 'AUD' }

export const ShippingAddress: FunctionComponent = () => {
  const { chosenAddress, pickupAddress, shippingAddressStatus, isShippingAddressChanged } = useShippingAddress()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const router = useAppRouter()
  const { error } = useShippingOption()
  const isDisabled = useIsDisabledUpdatingCheckout()
  const { t } = useTranslation()
  const isPickup = useIsPickup()
  const addressContainerRef = useRef(null)
  const { checkout, error: checkoutError } = useCheckoutState()
  const { sectionHeadingSize, smallTextSize } = useCheckoutSizes()
  const dispatch = useAppDispatch()
  const hasValidShippingAddress = useIsShippingAddressValid()
  const isDeferredShipping = useIsDeferredShipping()
  const { addressMode } = useInitialData()
  const isLockingShippingAddress = isPickup || addressMode === 'SHIP_TO_ORDER_ADDRESS'
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const showUnableToUpdateError = isDeferredShipping && checkoutError?.error === TerminalErrorCode.UnableToUpdateCheckout
  const navigateToShippingAddress = useNavigateToShippingAddress()

  const containerClassnames = clsx(!isLockingShippingAddress && styles.btnContainer)

  const isShippingAddressLoading = !isPickup && shippingAddressStatus <= ShippingAddressStatus.Initialising
  const shippingAddress = isPickup ? pickupAddress : chosenAddress

  const handleShippingAddressClick = (): void => {
    logEvent(TrackingEvent.PRESSED_CHANGE_SHIPPING_ADDRESS)
    navigateToShippingAddress()
  }

  const editShippingAddress = (): void => {
    logEvent(TrackingEvent.PRESSED_EDIT_SHIPPING_ADDRESS)
    router.push(editShippingAddressRoute(chosenAddress.id))
  }

  const shippingAddressError = (): string | JSX.Element | undefined => {
    if (error === ShippingOptionErrorType.ShippingAddressUnrecognised) {
      return (
        <Trans
          components={{ shippingAddressLink: <Link onClick={handleShippingAddressClick} kind='Underline' /> }}
          i18nKey='shippingOption:error.shippingAddress.unrecognizedShippingAddress'
          values={{ merchantName: checkout?.merchant?.name }}
        />
      )
    } else if (error === ShippingOptionErrorType.ShippingAddressUnsupported) {
      return t('shippingOption:error.shippingAddress.unsupportedShippingAddress')
    } else if (!isPickup && error === ShippingOptionErrorType.ShippingAddressIncomplete) {
      const editAddress = <Link onClick={editShippingAddress} kind='Underline' />
      return <Trans components={{ editAddress }} i18nKey='shippingOption:error.shippingAddress.incomplete' />
    }
  }

  const doUpdateCheckout = (): CheckoutAppDispatch => {
    // @ts-expect-error TODO: fix type error
    return dispatch(
      updateCheckout({
        amount: checkout?.amount ?? defaultMoney,
        shippingAddressId: isPickup ? undefined : chosenAddress.id,
      })
    )
  }

  const updateCheckoutIfRequired = (): void => {
    if (isShippingAddressChanged && isDeferredShipping) {
      try {
        const response = doUpdateCheckout()

        // @ts-expect-error TODO: fix type error
        unwrapResult(response)
      } catch (e) {
        capture.error('Error updating checkout on address change', { ...e })

        router.push(Route.TERMINAL_ERROR)
      }
    }
  }

  const renderContainer = (children: ReactNode): ReactNode => {
    const handleContainerClick = isLockingShippingAddress ? undefined : handleShippingAddressClick
    const shippingAddressErrorMessage = shippingAddressError()
    const hasError = shippingAddressErrorMessage !== undefined

    if (isArcadeDesignLanguage) {
      return (
        addressMode !== AddressModeType.NO_ADDRESS && (
          <>
            <Option
              text={
                <>
                  <Heading size={sectionHeadingSize} testNameSpace='shipping-address'>
                    {isPickup ? t('common:pickupFrom') : t('common:shipTo')}
                  </Heading>
                  {children}
                </>
              }
              caretPosition='Top'
              onClick={handleContainerClick}
              warningBorder={hasError}
            />
            {hasError && (
              <div className={styles.shippingAddressErrorContainer}>
                <Text color='Fire' size={smallTextSize} testNameSpace='shipping-address-error'>
                  {shippingAddressErrorMessage}
                </Text>
              </div>
            )}
          </>
        )
      )
    }

    return (
      <ModuleContainer
        padding={{ vertical: 'Large', horizontal: 'XLarge' }}
        showErrorBorder
        containerRef={addressContainerRef}
        errorText={shippingAddressErrorMessage}
        tabIndex={0}
        disabled={isDisabled || isShippingAddressLoading}
        className={containerClassnames}
        onClick={handleContainerClick}
        testNameSpace='shipping-address'
      >
        {children}
      </ModuleContainer>
    )
  }

  useEffect(() => {
    if (hasValidShippingAddress) {
      const valid = !isUndefinedOrEmpty(chosenAddress?.phoneNumber) && isValidAddress(chosenAddress)

      if (!valid && !isPickup && addressMode !== AddressModeType.NO_ADDRESS) {
        const incompleteAddressError = ShippingOptionErrorType.ShippingAddressIncomplete
        dispatch(setShippingOptionError(incompleteAddressError))
      }

      if (valid && error === ShippingOptionErrorType.ShippingAddressIncomplete) {
        dispatch(clearShippingOptionError())
      }

      if (valid || isPickup) {
        updateCheckoutIfRequired()
      }
    }
  }, [chosenAddress])

  if (addressMode === AddressModeType.NO_ADDRESS) {
    return null
  }
  return (
    <>
      {renderContainer(
        <Grid center>
          <ShippingAddressItem
            {...shippingAddress}
            disabled={isDisabled}
            preferred={false}
            isAddressLoading={isShippingAddressLoading}
            isAddressEmpty={!hasValidShippingAddress}
            boldNameLabel={!isArcadeDesignLanguage}
          />
          {!isArcadeDesignLanguage && !isLockingShippingAddress && (
            <Grid.FixedItem width={36} align='right'>
              <Icon.CaretRight size='Small' color={isDisabled || isShippingAddressLoading ? 'Gray80' : 'Black'} />
            </Grid.FixedItem>
          )}
        </Grid>
      )}

      <TooltipMessage addressContainerRef={addressContainerRef} />

      {showUnableToUpdateError && <ShippingOptionError error={TerminalErrorCode.UnableToUpdateCheckout} handleRetryButton={doUpdateCheckout} />}
    </>
  )
}
